import * as React from 'react';
import * as EcommscriptClient from './Api/ecommscriptClient';
import { BrowserRouter, Redirect, Route, Switch } from 'react-router-dom';
import Cookies from 'universal-cookie';
import { clone, isEqual, merge } from 'lodash';
import { BusinessUnit, BusinessUser, ConfigurationScope, InvitationTrigger, InvitationTriggerMap, PastOrdersAnalyze, Settings, TrustBoxEntitySettings, TrustBoxPage, UpdateType, Widget } from './Model';
import { Consent, Main, TrustBox } from './Containers';
import { Account, Auth, ExternalAuthentication, SIGNUP_EMAIL_KEY, SignupSuccessData } from '@trustpilot/ecommscript-integrationappauth/external-authentication';
import { AccessError, AppLock, Header, LinkError, NotificationAlert, NotificationType } from './CommonUI';
import { LocalizationProvider, Translations, Text } from 'ilenia';
import './Assets/styles/appFont.css';
import { LaunchDarkly } from 'react-launch-darkly';
import { css, StyleSheet } from 'aphrodite';
import { Common } from './Assets/styles/common';
import { addDomain, integrationConfig } from './Api/configapiClient';
import { getAnalyticsUserId, getReleases, getTierInfo } from './Api/settingsApiClient';
import { Tools } from './Tools';
import { getBusinessUser } from './Api/businessUserClient';
import { consentIsAccepted } from './Api/businessConsentsClient';
import { trackButton } from './Tracking';
import { Localization } from './Localization';
import { connect } from 'react-redux';
import { App as actions } from './Actions';
import * as analyticsActions from './Actions/analytics';
import * as pastOrdersActions from './Actions/PastOrders';
import { HttpError } from './CommonUI/UI/HttpError';

export interface AppState {
    settings: Settings;
    parent?: Window | null;
    referrer?: string;
    loggedIn: boolean;
    configurationScopeTree?: ConfigurationScope[];
    hasIntegrationKey: boolean;
    updateType: UpdateType;
    showUpdateNotification: boolean;
    signupEmail: string | null;
    showSettingsRestore?: boolean;
    remoteSettings?: Settings;
    businessInformation?: Account;
    tierName: string;
    businessUser: BusinessUser;
    mode?: string;
    consentAccepted: boolean;
    locale: string;
    translations: Translations;
    widgets: Widget[];
    originalSelectors: {
        skuSelector?: string;
        gtinSelector?: string;
        mpnSelector?: string;
    };
}

export interface AppProps {
    accessError: BusinessUnit;
    businessUnitId: string;
    setBusinessUser: (businessUser: BusinessUser) => void;
    setGlobalState: (state: any) => void;
    loadAnalytics: () => void;
    setPastOrdersAnalyze: (analyze: PastOrdersAnalyze) => void;
    setAccessError: (failedBusinessUnitId: string) => void;
}

class App extends React.Component<AppProps, AppState> {
    constructor(props: AppProps) {
        super(props);
        this.state = {
            settings: {
                general: {
                    key: '',
                    invitationTrigger: InvitationTrigger.OrderConfirmed,
                    mappedInvitationTrigger: [],
                },
                trustbox: {
                    trustboxes: [],
                    activeTrustbox: 0,
                },
                source: '',
                pluginVersion: '0.0.0',
                version: 'platform-version',
                pastOrders: {
                    showInitial: true,
                    syncInProgress: false,
                    synced: 0,
                    unsynced: 0,
                    failed: [],
                    analyze: undefined
                },
                gtinSelector: '',
                mpnSelector: '',
                skuSelector: '',
                isFromMarketplace: 'false',
                shop: '',
                stencilEnabled: false,
            },
            configurationScopeTree: [],
            referrer: '',
            loggedIn: false,
            hasIntegrationKey: false,
            consentAccepted: true,
            updateType: UpdateType.None,
            showUpdateNotification: false,
            signupEmail: '',
            showSettingsRestore: false,
            tierName: '',
            businessUser: {
                id: '',
                name: '',
                email: '',
                locale: '',
            },
            mode: undefined,
            locale: 'en-US',
            translations: Localization.getTranslations(),
            widgets: [],
            originalSelectors: {}
        };
        this.receiveSettings = this.receiveSettings.bind(this);
        this.changeSettings = this.changeSettings.bind(this);
        this.updateWindowSize = this.updateWindowSize.bind(this);
        this.sendPostMessage = this.sendPostMessage.bind(this);
        this.onConsentConfirm = this.onConsentConfirm.bind(this);
        this.onLoggedInChange = this.onLoggedInChange.bind(this);
        this.onConsentChange = this.onConsentChange.bind(this);
        this.checkVersion = this.checkVersion.bind(this);
        this.onShowUpdateNotificationClose = this.onShowUpdateNotificationClose.bind(this);
        this.logError = this.logError.bind(this);
        this.onRestoreSettings = this.onRestoreSettings.bind(this);
        this.handleLocalStorageChange = this.handleLocalStorageChange.bind(this);
        this.isJson = this.isJson.bind(this);
        this.onSignUp = this.onSignUp.bind(this);
        this.mapInvitationTrigger = this.mapInvitationTrigger.bind(this);
        this.getTierInfo = this.getTierInfo.bind(this);
        this.setWidgets = this.setWidgets.bind(this);
        this.loadAnalytics = this.loadAnalytics.bind(this);
        this.loadBusinessUserAndIdentify = this.loadBusinessUserAndIdentify.bind(this);
        this.getDebugData = this.getDebugData.bind(this);
    }

    public onShowUpdateNotificationClose() {
        this.saveRestoreNotification(false);
        this.setState({showUpdateNotification: false});
    }

    public saveRestoreNotification(value: boolean) {
        try {
            localStorage.setItem('tp_show_settings_restore_notification', JSON.stringify(value));
        } catch (e) {
            console.log('inaccessible local storage. Cannot save restore tifications flag');
        }
    }

    public loadBusinessUserAndIdentify() {
        getBusinessUser()
            .then((businessUser: BusinessUser) => {
                if (businessUser) {
                    this.props.setBusinessUser(businessUser);
                    getAnalyticsUserId(businessUser.email).then((analyticsUser) => {
                            if (analyticsUser) {
                                const { id, name, email, locale } = businessUser;
                                analytics.identify(analyticsUser.id, { id, name, email, locale });
                            }
                        }
                    );
                }
                this.setState({ businessUser });
            });
    }

    public componentDidUpdate(_: any, prevProps: any) {
        if (this.props.businessUnitId !== 'initial') {
            if (this.state.settings.pastOrders?.analyze !== undefined && prevProps.settings.pastOrders?.analyze !== this.state.settings.pastOrders?.analyze) {
                this.props.setPastOrdersAnalyze(this.state.settings.pastOrders.analyze);
            }
        }
    }

    public componentDidMount() {
        Auth.isLoggedIn(process.env.REACT_APP_IntegrationsExternalAuthAuthenticationApiUrl!, process.env.REACT_APP_IntegrationsExternalAuthCookiesDomain!).then(
            (isLoggedIn: boolean) => {
                if (isLoggedIn) {
                    this.loadBusinessUserAndIdentify();
                }
                this.setState({ loggedIn: isLoggedIn });
            }
        );

        try {
            window.addEventListener('storage', this.handleLocalStorageChange);
        } catch (e) {
            console.log('inaccessible local storage. Cannot attach event');
        }

        window.addEventListener('message', this.receiveSettings);
        this.replaceLogError();
        this.onElementHeightChange(window.document.body, () => {
            this.updateWindowSize();
        });

        try {
            const storedValue = window.localStorage ? localStorage.getItem('tp_show_settings_restore_notification') : false;
            const showUpdateNotification = storedValue ? JSON.parse(storedValue) === true : false;
            const signupEmail = window.localStorage ? localStorage.getItem(SIGNUP_EMAIL_KEY) : '';
            this.setState({ showUpdateNotification, signupEmail });
        } catch (e) {
            console.log('inaccessible local storage. Update notification issue');
        }

    }

    public componentWillUnmount() {
        window.removeEventListener('message', this.receiveSettings);
    }

    public replaceLogError() {
        const oldLog = console.error;
        console.error = (message?: any, ...optionalParams: any[]) => {
            this.logError(message, new Error().stack, optionalParams);
            oldLog.call(console, message, optionalParams);
        };
    }

    public handleLocalStorageChange(e: StorageEvent) {
        if (e.key === 'tp_trustbox_trustboxes' && e.newValue) {
            try {
                const data = JSON.parse(e.newValue);
                if (data) {
                    this.changeSettings({ trustbox: data }, () => this.sendPostMessage('submit'));
                }
            } catch (e) {
                this.logError(`Failed to handle local storage settings. ${e.newValue}`);
            }
        } else if (e.key === 'tp_data_transfer' && e.newValue) {
            this.sendPostMessage(e.newValue);
        }
    }

    public logError(message?: any, trace?: string, optionalParams?: any[]) {
        const { settings } = this.state;
        EcommscriptClient.log(JSON.parse(JSON.stringify({
            key: settings && settings.general ? settings.general.key : '',
            message,
            trace,
            optionalParams,
        })));
    }

    public checkVersion(source: string, version: string): Promise<UpdateType> {
        return getReleases(source)
            .then((result) => {
                if (result && Array.isArray(result)) {
                    let updateType = UpdateType.None;
                    (result).forEach((element) => {
                        if (element.minVersion) {
                            if (Tools.compareVersions(element.version, version, '>')) {
                                updateType = UpdateType.Critical;
                            }
                        }
                        if (element.latestVersion) {
                            if (updateType !== UpdateType.Critical && Tools.compareVersions(element.version, version, '>')) {
                                updateType = UpdateType.Minor;
                            }
                        }
                    });
                    return updateType;
                }
                return UpdateType.None;
            });
    }

    public onConsentConfirm(businessUnitId: string) {
        integrationConfig(businessUnitId, { integrationStatus: 'Active' })
            .then((settings) => {
                let newSettings: any;
                if (settings && settings.pluginSettings && settings.pluginSettings.general && settings.pluginSettings.general.key) {
                    newSettings = {
                        settings: merge(this.state.settings, {
                            general: {
                                key: settings.pluginSettings.general.key
                            },
                        }),
                        hasIntegrationKey: true,
                        consentAccepted: true,
                        showUpdateNotification: false,
                        showSettingsRestore: (settings.pluginSettings.source === this.state.settings.source) && this.state.mode !== 'trustbox-only',
                        remoteSettings: settings.pluginSettings,
                    };
                } else if (settings.key) {
                    newSettings = {
                        settings: merge(this.state.settings, {
                            general: {
                                key: settings.key
                            },
                        }),
                        hasIntegrationKey: true,
                        consentAccepted: true,
                        showUpdateNotification: false,
                    };

                    if (settings.pluginSettings && settings.pluginSettings.stores) {
                        // restore TrustBox from old integration
                        const trustBoxSetting: any = this.getConfigTrustbox(settings.pluginSettings!);
                        if (trustBoxSetting) {
                            newSettings = merge(newSettings, {
                                showUpdateNotification: true,
                                showSettingsRestore: (settings.pluginSettings.source === this.state.settings.source) && this.state.mode !== 'trustbox-only',
                                remoteSettings: clone(newSettings.settings)
                            });
                            newSettings.remoteSettings.trustbox!.trustboxes = [trustBoxSetting];
                            this.saveRestoreNotification(true);
                        }
                    }
                }
                if (newSettings) {
                    // add domain to referring names for shopify
                    const { source, shop, general: { key } } = newSettings.settings;
                    if (key && source && shop && ['shopify', 'bigcommerce'].includes(source.toLowerCase())) {
                        addDomain(key, shop);
                    }

                    this.setState(newSettings, () => {
                        this.sendPostMessage('submit');
                        // set default value to false if enabledProductReviews is not set
                        // (not set is treated as true elsewere because of the history)
                        if (settings.enableProductReviews === undefined) {
                            integrationConfig(businessUnitId, { enableProductReviews: false });
                        }
                    });
                } else {
                    this.setState({ hasIntegrationKey: true, consentAccepted: true, });
                }
            }).then(() => {
                const { configurationScopeTree } = this.state;
                if (configurationScopeTree) {
                    integrationConfig(businessUnitId, { configurationScopeTree });
                }
            }).then(() => {
                const { settings, tierName, configurationScopeTree } = this.state;
                this.props.setGlobalState({
                    isFetching: false,
                    integrationKey: settings!.general!.key,
                    source: settings.source,
                    productIdentificationOptions: settings.productIdentificationOptions,
                    tierName,
                    configurationScopeTree,

                });
            }).catch((err) => {
                this.setState(
                    {
                        hasIntegrationKey: true,
                        consentAccepted: true,
                    },
                    () => this.logError(err.message, err.stack)
                );
            });
    }

    public getDebugData() {
        return {
            settings: this.state.settings,
            loggedIn: this.state.loggedIn,
            hasIntegrationKey: this.state.hasIntegrationKey,
            consentAccepted: this.state.consentAccepted,
            signupEmail: this.state.signupEmail,
            tierName: this.state.tierName,
            businessUser: this.state.businessUser,
        };
    }

    public onLoggedInChange(loggedIn: boolean) {
        try {
            EcommscriptClient.logError({
                message: `Logged in - ${loggedIn}`,
                error: `Stack: ${new Error().stack}, State ${JSON.stringify(this.getDebugData())}`
            });
        } catch {
            // just to be safe it does not impact anything around
        }
        const { accessError, setAccessError } = this.props;
        trackButton({
            placement: 'Bottom',
            action: 'Login completed',
            name: 'login-completed',
            source: this.state.settings?.source || '',
            valid: String(loggedIn && !accessError),
            tierName: this.state.tierName,
        });
        if (!loggedIn && !accessError) {
            const { businessUnitId } = this.props;
            EcommscriptClient.logError({
                message: `Showing access error for BUID ${businessUnitId}`,
                error: `State ${JSON.stringify(this.getDebugData())}`
            });
            NotificationAlert.alert(NotificationType.Error, 'login.error');
        }

        if (loggedIn) {
            this.loadBusinessUserAndIdentify();
        }

        if (accessError) {
            setAccessError('');
        }

        this.setState({ loggedIn });
    }

    public onConsentChange(consentAccepted: boolean) {
        this.setState({ consentAccepted });
    }

    public getConfigTrustbox(pluginSettings: Settings) {
        let trustBox = pluginSettings!.stores[0].trustbox;

        // check if it is correct trustbox and there is no different settings per store
        pluginSettings!.stores.forEach((element: any) => {
            if (!(isEqual(element.trustbox, trustBox) &&
                element.trustbox &&
                element.trustbox.enabled &&
                element.trustbox.snippet &&
                element.trustbox.xpath &&
                element.trustbox.page)) {
                trustBox = undefined;
            }
        });

        // change trustbox page
        if (trustBox) {
            switch (trustBox.page.toLowerCase()) {
                case 'trustpilot_trustbox_product':
                    trustBox.page = TrustBoxPage.Product;
                    break;
                case 'trustpilot_trustbox_homepage':
                    trustBox.page = TrustBoxPage.Landing;
                    break;
                case 'trustpilot_trustbox_category':
                    trustBox.page = TrustBoxPage.Category;
                    break;
                default:
                    return;
            }
        }

        const base64pattern = /^([A-Za-z0-9+/]{4})*([A-Za-z0-9+/]{3}=|[A-Za-z0-9+/]{2}==)?$/;
        const xpathFormatValid = base64pattern.test(trustBox.xpath);
        trustBox.xpath = xpathFormatValid ? trustBox.xpath : btoa(trustBox.xpath);

        trustBox.xpaths = btoa(JSON.stringify([atob(trustBox.xpath)]));
        delete trustBox.xpath;

        return trustBox;
    }

    public isJson(str: string) {
        try {
            JSON.parse(str);
        } catch (e) {
            return false;
        }
        return true;
    }

    public receiveSettings(e: MessageEvent) {
        if (e.data && this.isJson(e.data)) {
            const data = JSON.parse(e.data);
            if (data.basis && data.basis === 'plugin') {
                delete data.basis;
                if (data.general && data.general.key === '') {
                    delete data.general.invitationTrigger;
                    if (data.trustbox && data.trustbox.pageUrls) {
                        delete data.trustbox.trustboxes;
                        data.trustbox.pageUrls = {
                            landing: data.trustbox.pageUrls.landing,
                            category: data.trustbox.pageUrls.category,
                            product: data.trustbox.pageUrls.product,
                        };
                    }
                }
                const newSettings = {
                    settings: merge(this.state.settings, data),
                    parent: this.state.parent ? this.state.parent : this.loadAnalytics(e.source as Window),
                    referrer: this.state.referrer ? this.state.referrer : e.origin,
                    hasIntegrationKey: this.state.hasIntegrationKey,
                    configurationScopeTree: this.state.configurationScopeTree,
                    businessInformation: this.state.businessInformation,
                    updateType: UpdateType.None,
                    tierName: '',
                    mode: this.state.mode,
                };
                if (data.general) {
                    newSettings.hasIntegrationKey = data.general.key && data.general.key.length > 0;
                    newSettings.settings.general!.mappedInvitationTrigger = this.mapInvitationTrigger(data.source, newSettings.settings);
                }
                if (data.configurationScopeTree) {
                    newSettings.configurationScopeTree = data.configurationScopeTree;
                }
                if (data.pastOrders && data.pastOrders.failed !== undefined) {
                    newSettings.settings.pastOrders.failed = data.pastOrders.failed;
                }
                if (data.pastOrders && data.pastOrders.analyze !== undefined) {
                    newSettings.settings.pastOrders.analyze = data.pastOrders.analyze;
                }
                if (data.businessInformation !== undefined) {
                    newSettings.businessInformation = data.businessInformation;
                }
                if (data.mode !== undefined) {
                    newSettings.mode = data.mode;
                }

                if (data.customTrustBoxes !== undefined) {
                    try {
                        localStorage.setItem('tp_custom_trustboxes', data.customTrustBoxes);
                    } catch (e) {
                        console.log('inaccessible local storage. custom trustboxes cannot be set');
                    }
                }

                if (Localization.allowedLocales.includes(data.locale)) {
                    this.setState({locale: data.locale, translations: Localization.getTranslations(data.locale)});
                }
                this.setState({
                    ...newSettings,
                    originalSelectors: {
                        skuSelector: newSettings.settings.skuSelector,
                        gtinSelector: newSettings.settings.gtinSelector,
                        mpnSelector: newSettings.settings.mpnSelector,
                    }
                });

                Promise.all([
                    this.checkVersion(newSettings.settings.source, newSettings.settings.pluginVersion),
                    this.getTierInfo(newSettings.settings.general.key)
                ]).then(([updateType, tierName]) => {
                    this.setState({ updateType, tierName });
                    this.props.setGlobalState({
                        isFetching: false,
                        integrationKey: newSettings.settings.general.key,
                        source: newSettings.settings.source,
                        productIdentificationOptions: newSettings.settings.productIdentificationOptions,
                        tierName,
                        configurationScopeTree: newSettings.configurationScopeTree,

                    });
                });
            } else if (data.basis && data.basis === 'iframe') {
                if (data.trustboxes) {
                    this.changeSettings({ trustbox: { trustboxes: data.trustboxes } }, () => this.sendPostMessage('submit'));
                }
                if (data.track && data.track.event) {
                    analytics.track(data.track.event, data.track.props);
                }
            } else if (data.basis && data.basis === 'storefront') {
                if (data.trustboxes) {
                    this.changeSettings({ trustbox: { trustboxes: data.trustboxes, dirty: true } });
                }
            } else if (data.trustpilot_signup_data) {
                const signupData = JSON.parse(atob(data.trustpilot_signup_data));
                this.setState({businessInformation: signupData});
            }
        }
    }

    public onElementHeightChange(elm: HTMLElement, callback: () => void) {
        let lastHeight = elm.clientHeight;
        let newHeight;
        let onElementHeightChangeTimer;
        (function run() {
            newHeight = elm.clientHeight;
            if (lastHeight !== newHeight) {
                callback();
            }
            lastHeight = newHeight;

            if (onElementHeightChangeTimer) {
                clearTimeout(onElementHeightChangeTimer);
            }
            onElementHeightChangeTimer = setTimeout(run, 200);
        })();
    }

    public sendPostMessage(message: string | object) {
        const { settings, parent, referrer } = this.state;

        if (typeof message === 'object') {
            message = JSON.stringify(message);
        }

        if (typeof message === 'string' && message === 'submit') {
            message = JSON.stringify({ settings, type: message });
            if (settings && settings.general && settings.general.key) {
                EcommscriptClient.postSettings(settings.general.key, JSON.stringify(settings));
            }
        }

        if (parent) {
            parent.postMessage(message, referrer!);
        }
    }

    public updateWindowSize() {
        const body = document.body;
        const html = document.documentElement!;
        const scrollHeight = Math.min(body.scrollHeight, html.scrollHeight);
        const computedHeight = Math.max(scrollHeight, body.offsetHeight, html.offsetHeight);
        const windowSettings = { window: { height: computedHeight} };
        if (this.state.parent) {
            this.state.parent.postMessage(JSON.stringify(windowSettings), this.state.referrer!);
        }
    }

    public changeSettings(settings: Settings, cb?: () => void) {
        const newSettings = {
            settings: merge(this.state.settings, settings),
        };
        if (settings.general && settings.general.invitationTrigger) {
            const { source } = this.state.settings;
            newSettings.settings.general!.mappedInvitationTrigger = this.mapInvitationTrigger(source!, settings);
        }

        if (settings.trustbox) {
            newSettings.settings.trustbox!.trustboxes = [...settings.trustbox.trustboxes!];
        }

        this.setState(newSettings, () => {
            if (settings.general && settings.general.key) {
                getTierInfo(settings.general.key);
            }
            if (cb) { cb(); }
        });
    }

    public sendTrustBoxMessage(element: JSX.Element | null, enable: boolean) {
        const { source, stencilEnabled } = this.state.settings;
        return () => {
            if (!(source === 'Shopify' || (source === 'BigCommerce' && !stencilEnabled))) {
                const TrustBoxPreviewMode = {
                    TrustBoxPreviewMode: {
                        enable,
                        integrationKey: this.state.settings.general!.key,
                        source
                    },
                    basis: 'iframe',
                };
                this.sendPostMessage(TrustBoxPreviewMode);
            }
            return element;
        };
    }

    public sendTrustBoxes(trustboxes: TrustBoxEntitySettings[]) {
        const TrustBoxData = {
            TrustBoxData: {
                trustboxes,
            },
            basis: 'iframe',
            requireReload: true,
        };
        this.sendPostMessage(TrustBoxData);
    }

    public onRestoreSettings(restore: boolean) {
        if (restore && this.state.remoteSettings) {
            // overwrite current (default) settings with settings stored in S3
            const settings = this.state.remoteSettings;
            const newSettings = {
                settings: merge(this.state.settings, {
                    general: {
                        key: settings.general!.key,
                        invitationTrigger: settings.general!.invitationTrigger,
                    },
                    trustbox: {
                        trustboxes: settings.trustbox ? settings.trustbox.trustboxes : undefined,
                        pageUrls: settings.trustbox ? settings.trustbox.pageUrls : undefined,
                    },
                    gtinSelector: settings.gtinSelector,
                    mpnSelector: settings.mpnSelector,
                    skuSelector: settings.skuSelector,
                }),
                hasIntegrationKey: true,
                showUpdateNotification: true,
                showSettingsRestore: false,
            };
            newSettings.settings.general!.mappedInvitationTrigger = this.mapInvitationTrigger(this.state.settings.source!, settings);
            this.saveRestoreNotification(true);
            this.setState(newSettings, () => {
                this.sendPostMessage('submit');
                this.sendPostMessage({ pageUrls: newSettings.settings.trustbox.pageUrls , type: 'updatePageUrls' });
                this.sendTrustBoxes(newSettings.settings.trustbox.trustboxes);
            });
            NotificationAlert.alert(NotificationType.Success, 'settingsRestore.success');
        } else {
            const newSettings = {
                showSettingsRestore: false,
                settings: merge(this.state.settings, {
                    pastOrders: { showInitial: true }
                }),
            };
            this.setState(newSettings);
            this.saveRestoreNotification(false);
            this.sendPostMessage(`showPastOrdersInitial:true`);
            if (newSettings.settings.general && newSettings.settings.general.key) {
                const integrationKey = newSettings.settings.general.key;
                consentIsAccepted(integrationKey).then((consent) => {
                    if (consent && consent.businessUnitId) {
                        integrationConfig(consent.businessUnitId, {
                            enableProductReviews: false,
                            templateMap: [],
                        }).catch((err) => this.logError(err.message, err.stack));
                    }
                }).catch((err) => this.logError(err.message, err.stack));
            }
        }
    }

    public setWidgets(widgets: Widget[]) {
        this.setState({ widgets }, () => this.sendPostMessage({basis: 'iframe', widgets}));
    }

    public onSignUp(data: SignupSuccessData) {
        try {
            if (data && data.email) {
                localStorage.setItem(SIGNUP_EMAIL_KEY, data.email);
            }
        } catch (e) { /* empty by purpose */ }

        if (data && data.integrationConfig?.key) {
            this.changeSettings({
                general: {
                    key: data.integrationConfig?.key,
                }
            }, () => {
                this.sendPostMessage('submit');
                this.setState({ hasIntegrationKey: true });
                window.location.reload();
            });
        }
    }

    public render() {
        const { accessError } = this.props;
        const { settings, loggedIn, consentAccepted, hasIntegrationKey, referrer, updateType, showUpdateNotification,
                showSettingsRestore, parent, configurationScopeTree,  businessInformation, tierName, businessUser, mode,
                locale, translations, widgets, signupEmail, originalSelectors } = this.state;
        const forceUpdate = updateType === UpdateType.Critical;
        if (window.location.protocol === 'http:') {
            return (
                <LocalizationProvider locale={locale} translations={translations}>
                    <HttpError/>
                </LocalizationProvider>
            );
        }
        const renderHeader = (showMenu: boolean) => (
            <Header
                pluginVersion={settings.pluginVersion!}
                showMenu={showMenu}
                source={settings.source!}
                disabled={forceUpdate || showSettingsRestore!}
                notify={updateType === UpdateType.Minor}
                mode={mode}
            />
        );
        const renderTrustBox = () => (
            <TrustBox
                settings={settings}
                parent={parent}
                changeSettings={this.changeSettings}
                tierName={tierName}
                locale={locale}
                widgets={widgets}
                setWidgets={this.setWidgets}
            />
        );
        const checkStorageAccesibility = () => {
            let result = true;
            try {
                localStorage.setItem('Trustpilot_test', 'test');
                const localStorageResult = localStorage.getItem('Trustpilot_test');
                if (localStorageResult !== 'test') {
                    result = false;
                }
                localStorage.removeItem('Trustpilot_test');
                const cookies = new Cookies();
                cookies.set('Trustpilot_test', 'test', {
                    path: '/',
                    domain: process.env.REACT_APP_CookiesDomain!,
                    sameSite: 'none',
                    secure: true
                });
                const cookieResult = cookies.get('Trustpilot_test');
                if (cookieResult !== 'test') {
                    result = false;
                }
                cookies.remove('Trustpilot_test');
            } catch {
                result = false;
            }

            if (!result) {
                const { businessUnitId } = this.props;
                EcommscriptClient.logError({
                    message: `Do not show login form for BUID ${businessUnitId}`,
                    error: `state -> ${JSON.stringify(this.getDebugData())}`
                });
            }
            return result;
        };

        const renderAuth = () => {
            if (!(settings.engagement && settings.engagement.source && settings.engagement.shop)) {
                settings.engagement = {
                    date: Date.now().toString(),
                    source: settings.source,
                    shop: settings.shop || this.state.businessInformation?.website,
                };
            }
            if (accessError) {
                EcommscriptClient.logError({
                    message: `Generic AccessError `,
                    error: `BUID details ${JSON.stringify(accessError)} and state -> ${JSON.stringify(this.getDebugData())}`
                });
            }

            // let's not pass trustbox, pastOrders and tree props to auth as their size might be too long for url
            const { trustbox, pastOrders, configurationScopeTree: tree, ...rest } = settings;

            // integration_config is only ever read by the signup path, where it seeds the config for a
            // brand new business unit. Nothing in the login flow looks at it. A populated general.key
            // means this plugin is already connected, so the merchant can only be logging in -- omit the
            // payload entirely rather than spending ~700 url characters on it. See INTE-3262.
            const isAlreadyConnected = Boolean(rest.general && rest.general.key);

            return (
                <React.Fragment>
                    <h1>
                        <Text id="login.title" />
                    </h1>
                    {accessError && <AccessError businessUnit={accessError} />}
                    <ExternalAuthentication
                        source={settings.source!}
                        locale={locale}
                        integrationConfig={isAlreadyConnected ? undefined : rest}
                        trustpilotDomain={process.env.REACT_APP_IntegrationsExternalAuthTrustpilotDomain!}
                        appClientId={process.env.REACT_APP_IntegrationsExternalAuthAppClientId!}
                        redirectUri={window.location.origin}
                        cookiesDomain={process.env.REACT_APP_IntegrationsExternalAuthCookiesDomain!}
                        authenticationApiUrl={process.env.REACT_APP_IntegrationsExternalAuthAuthenticationApiUrl!}
                        onLoggedIn={this.onLoggedInChange}
                        onSignedUp={this.onSignUp}
                    />
                </React.Fragment>
            );
        };

        const shouldRedirectToUpdate = forceUpdate && location.pathname !== '/update';

        return (<LocalizationProvider locale={locale} translations={translations}>
                    <BrowserRouter>
                        <LaunchDarkly clientId={process.env.REACT_APP_LaunchDarklyID} user={{ key: 'anonymous', anonymous: true }}>
                            {referrer ?
                            <div className={css(styles.text)}>
                                {signupEmail
                                ? <AppLock
                                    integrationKey={settings.general!.key!}
                                />
                                : <Switch>
                                    {shouldRedirectToUpdate && <Redirect to="/update" />}
                                    {shouldRedirectToUpdate && <Redirect to="/update" />}
                                    <Route
                                        path="/trustbox"
                                        render={this.sendTrustBoxMessage(
                                            <React.Fragment>
                                                {renderHeader(loggedIn)}
                                                {renderTrustBox()}
                                            </React.Fragment>
                                        , true)}
                                    />
                                    <Route
                                        path="/signup"
                                        render={this.sendTrustBoxMessage(
                                            <React.Fragment>
                                                {renderHeader(false)}
                                                <main className={css(styles.container)}>
                                                    {renderAuth()}
                                                </main>
                                            </React.Fragment>
                                        , false)}
                                    />
                                    <Route
                                        path="*"
                                        render={this.sendTrustBoxMessage(
                                            <React.Fragment>
                                                {renderHeader(loggedIn && hasIntegrationKey && consentAccepted && !accessError)}
                                                <main className={css(styles.container)}>
                                                    {checkStorageAccesibility() ?
                                                        (!loggedIn || accessError
                                                        ? renderAuth()
                                                        : (!hasIntegrationKey || !consentAccepted)
                                                            ? <Consent
                                                                onConfirm={this.onConsentConfirm}
                                                                source={settings.source!}
                                                                isFromMarketplace={settings.isFromMarketplace!}
                                                                tierName={tierName}
                                                                mode={mode}
                                                                locale={locale}
                                                            />
                                                            : <React.Fragment>
                                                                <Main
                                                                    settings={settings}
                                                                    onChange={this.changeSettings}
                                                                    sendPostMessage={this.sendPostMessage}
                                                                    integrationKey={settings.general!.key!}
                                                                    loggedIn={loggedIn && !accessError}
                                                                    onLoggedIn={this.onLoggedInChange}
                                                                    forceUpdate={forceUpdate}
                                                                    showUpdateNotification={showUpdateNotification}
                                                                    onShowUpdateNotificationClose={this.onShowUpdateNotificationClose}
                                                                    showSettingsRestore={showSettingsRestore!}
                                                                    onRestoreSettings={this.onRestoreSettings}
                                                                    configurationScopeTree={configurationScopeTree}
                                                                    businessInformation={businessInformation}
                                                                    tierName={tierName}
                                                                    businessUser={businessUser}
                                                                    mode={mode}
                                                                    onConsent={this.onConsentChange}
                                                                    originalSelectors={originalSelectors}
                                                                />
                                                            </React.Fragment>
                                                        ) : <div className={css(styles.errorContainer)}>
                                                                <LinkError text={'login.thirdparty'} link={'https://support.trustpilot.com/hc/en-us/articles/360021100439-Trustpilot-ecommerce-integration-troubleshooting-Login-issues#enable-3rd-party-cookies-0'} />
                                                            </div>
                                                    }
                                                </main>
                                            </React.Fragment>
                                        , false)}
                                    />
                                </Switch>
                                }
                                {!accessError && <NotificationAlert />}
                            </div> : <div/>}
                        </LaunchDarkly>
                    </BrowserRouter>
                </LocalizationProvider>
        );
    }

    private loadAnalytics(source: Window | null) {
        this.props.loadAnalytics();
        return source;
    }

    private mapInvitationTrigger(source: string, settings: Settings) {
        const { invitationTrigger } = settings.general!;
        return [...InvitationTriggerMap[source!.toLowerCase()][invitationTrigger!]];
    }

    private getTierInfo(integrationKey?: string): Promise<string> {
        return new Promise((resolve) => {
            if (this.state.loggedIn && integrationKey) {
                return getTierInfo(integrationKey)
                    .then((tierName: string) => resolve(tierName));
            }
            return resolve('');
        });
    }
}

const styles = StyleSheet.create({
    container: {
        borderRadius: Common.Borders.radius,
        backgroundColor: Common.Color.lightTextColor,
        border: Common.Borders.border,
        padding: '30px',
        marginBottom: '75px',
    },
    text: {
        font: Common.Font.normalText,
    },
    errorContainer: {
        marginBottom: '25px',
    }
});

const mapStateToProps = (state: any) => ({
    accessError: state.App.accessError,
    businessUnitId: state.App.account.businessUnitId
});

const mapDispatchToProps = (dispatch: any) => ({
    setBusinessUser: (businessUser: BusinessUser) => dispatch(actions.setBusinessUser(businessUser)),
    setGlobalState: (state: any) => dispatch(actions.setGlobalState(state)),
    loadAnalytics: () => dispatch(analyticsActions.loadAnalytics()),
    setPastOrdersAnalyze: (analyze: PastOrdersAnalyze) => dispatch(pastOrdersActions.receiveAnalyze(analyze)),
    setAccessError: (failedBusinessUnitId: string) => dispatch(actions.setAccessError(failedBusinessUnitId)),
});

export default connect(mapStateToProps, mapDispatchToProps)(App);
export { App };
